import type { Translate } from '../../../i18n/I18nProvider';
import type { ProfileLimits } from './rules';

/** The request field that carries the picture; a refusal about the picture names it. */
export const PICTURE_FIELD = 'profile_picture_media_id';

/**
 * A failed save, ready to show: one sentence for what went wrong, what to do
 * next, the fields to mark, and whether to list everything WhatsApp checks.
 *
 * channel-wa answers a refused profile write with a code (15 October 2026),
 * and the words are this console's own, by code, so they are in the person's
 * language and the limits are the ones the page counts against. A code this
 * page has no words for shows the platform's own sentence, which is written
 * for a shop owner already. An answer with no readable body (a proxy's error
 * page, a dropped connection mid-way) never shows a raw status: it gets the
 * list of what WhatsApp checks.
 */
export interface SaveErrorView {
  code: string;
  message: string;
  next?: string;
  /** Show the list of what WhatsApp accepts, and the "still fails" sentence. */
  checklist: boolean;
  /** The trace id to quote, when the platform sent one. */
  reference?: string;
  /** Messages to put on fields, by the request's field names. */
  fieldErrors: Record<string, string>;
  /** Nothing the person can change: shown as a notice, not an error. */
  platform: boolean;
}

interface ErrorBody {
  code?: unknown;
  message?: unknown;
  next_step?: unknown;
  action?: unknown;
  fault?: unknown;
  retry_after?: unknown;
  field?: unknown;
  problems?: unknown;
  request_id?: unknown;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.length > 0 ? v : undefined);

function problemsOf(body: ErrorBody): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(body.problems)) return out;
  for (const p of body.problems as { field?: unknown; message?: unknown }[]) {
    const field = str(p?.field);
    const message = str(p?.message);
    if (field && message && !out[field]) out[field] = message;
  }
  return out;
}

/** The rule for a field Meta refused, in this console's words and with the real limits. */
export function fieldRule(field: string, t: Translate, limits: ProfileLimits, fallback?: string): string {
  const mb = Math.round(limits.picture_bytes / (1024 * 1024));
  switch (field) {
    case 'about': return t('numbers.profile.error.field.about', { max: limits.about });
    case 'description': return t('numbers.profile.error.field.description', { max: limits.description });
    case 'address': return t('numbers.profile.error.field.address', { max: limits.address });
    case 'email': return t('numbers.profile.error.field.email', { max: limits.email });
    case 'websites': return t('numbers.profile.error.field.websites', { count: limits.websites, max: limits.website });
    case 'vertical': return t('numbers.profile.error.field.vertical');
    case PICTURE_FIELD: return t('numbers.profile.error.field.picture', { min: limits.picture_min_edge, max: mb });
    default: return fallback ?? t('numbers.profile.error.PROFILE_REFUSED.message');
  }
}

export function readSaveError(
  e: unknown,
  t: Translate,
  limits: ProfileLimits,
  formatTime: (iso: string) => string
): SaveErrorView {
  const name = (e as { name?: unknown } | null)?.name;
  if (name === 'NetworkError' || name === 'TimeoutError') {
    return { code: 'NETWORK', message: t('numbers.profile.error.network'), checklist: false, fieldErrors: {}, platform: false };
  }

  const api = e as { body?: unknown; requestId?: unknown } | null;
  const raw = api?.body;
  const body: ErrorBody | null = raw && typeof raw === 'object' && 'error' in raw
    && (raw as { error?: unknown }).error && typeof (raw as { error: ErrorBody }).error.code === 'string'
    ? (raw as { error: ErrorBody }).error
    : null;

  if (!body) {
    return {
      code: 'UNREADABLE',
      message: t('numbers.profile.error.unreadable'),
      next: t('numbers.profile.error.PROFILE_REFUSED.next'),
      checklist: true,
      reference: str(api?.requestId),
      fieldErrors: {},
      platform: false
    };
  }

  const code = String(body.code);
  const reference = str(body.request_id) ?? str(api?.requestId);
  const serverMessage = str(body.message) ?? t('numbers.profile.error.PROFILE_REFUSED.message');
  const serverNext = str(body.next_step);
  const problems = problemsOf(body);

  switch (code) {
    case 'PROFILE_INVALID':
      // The service's own check, with each problem named on its field.
      return { code, message: t('numbers.profile.error.PROFILE_INVALID'), checklist: false, fieldErrors: problems, platform: false };

    case 'PROFILE_PICTURE_TOO_SMALL': {
      const message = t('numbers.profile.error.PROFILE_PICTURE_TOO_SMALL.message');
      const next = t('numbers.profile.error.PROFILE_PICTURE_TOO_SMALL.next', { size: limits.picture_edge });
      return { code, message, next, checklist: false, reference, fieldErrors: { [PICTURE_FIELD]: `${message} ${next}` }, platform: false };
    }

    case 'PROFILE_FIELD_REFUSED': {
      const field = str(body.field) ?? Object.keys(problems)[0] ?? '';
      const rule = fieldRule(field, t, limits, problems[field] ?? serverMessage);
      return {
        code, message: rule, next: t('numbers.profile.error.PROFILE_FIELD_REFUSED.next'), checklist: false, reference,
        fieldErrors: field ? { [field]: rule } : {}, platform: false
      };
    }

    case 'PROFILE_RATE_LIMITED': {
      const at = str(body.retry_after);
      return {
        code,
        message: t('numbers.profile.error.PROFILE_RATE_LIMITED.message'),
        next: at
          ? t('numbers.profile.error.PROFILE_RATE_LIMITED.nextAt', { time: formatTime(at) })
          : t('numbers.profile.error.PROFILE_RATE_LIMITED.next'),
        checklist: false, reference, fieldErrors: {}, platform: false
      };
    }

    case 'PROFILE_META_TEMPORARY':
      return {
        code, message: t('numbers.profile.error.PROFILE_META_TEMPORARY.message'),
        next: t('numbers.profile.error.PROFILE_META_TEMPORARY.next'), checklist: true, reference, fieldErrors: {}, platform: false
      };

    case 'PROFILE_REFUSED':
      return {
        code, message: t('numbers.profile.error.PROFILE_REFUSED.message'),
        next: t('numbers.profile.error.PROFILE_REFUSED.next'), checklist: true, reference, fieldErrors: {}, platform: false
      };

    case 'NO_ACCESS':
      return {
        code, message: t('numbers.profile.error.NO_ACCESS.message'),
        next: t('numbers.profile.error.NO_ACCESS.next'), checklist: false, reference, fieldErrors: {}, platform: true
      };

    default:
      // A code this page has no words for: the platform's own sentences, and any fields it named.
      return {
        code, message: serverMessage, next: serverNext, checklist: false, reference,
        fieldErrors: problems, platform: body.fault === 'platform'
      };
  }
}
